/**
 * @fileoverview Shared run-flow column-mapping step (Map Columns).
 * @module import-editor/runFlow/steps/RunColumnMappingStep
 *
 * Responsibilities:
 * - Render the run-intent column mapping body: shell-template banner,
 *   mode-specific help text, lookup target-field seeding, and the shared
 *   RunWizardColumnMappingPanel wired to the host adapter's suggest /
 *   interpret endpoints.
 *
 * Dependencies:
 * - `../../components/RunWizardColumnMappingPanel` — the shared mapping grid.
 * - `../useImportRunFlow` — the shared state machine (via the `flow` prop).
 *
 * Architecture:
 * - Presentation-only; mapping mutations flow to the hook via
 *   `flow.patch({ fieldMappings })` and AI/rule suggestions go through the
 *   host's services adapter (never a direct API call).
 *
 * Notes:
 * - Extracted from tenant-web's ImportWizardContainer
 *   (ColumnMappingStepContent + LOOKUP_TARGET_FIELDS) during the Phase 2A
 *   run-flow migration (G-002). system-web previously lacked the lookup
 *   seeding and shell banner; both hosts now share this single body.
 */

import { useEffect } from 'react';
import type { FieldMapping, ImportDataMode } from '@firesquirrel/local-db';
import RunWizardColumnMappingPanel from '../../components/RunWizardColumnMappingPanel';
import type { ImportRunFlow } from '../useImportRunFlow';

// Lookup mode target fields. The lookup schema is defined by the backend
// lookup importer; both hosts share the same lookup collection contract, so
// the catalog lives here in the shared run flow.
const LOOKUP_TARGET_FIELDS: { field: string; label: string; required?: boolean }[] = [
  { field: 'lookupType', label: 'Lookup Type', required: true },
  { field: 'code',       label: 'Code',        required: true },
  { field: 'name',       label: 'Name',        required: true },
  { field: 'description',       label: 'Description' },
  { field: 'sortOrder',         label: 'Sort Order' },
  { field: 'isActive',          label: 'Active' },
  { field: 'category',          label: 'Category' },
  { field: 'appliesTo',         label: 'Applies To' },
  { field: 'inheritancePolicy', label: 'Inheritance Policy' },
  { field: 'scope',             label: 'Scope' },
  { field: 'display.textColor',        label: 'Text Color' },
  { field: 'display.backgroundColor', label: 'Background Color' },
  { field: 'display.icon',             label: 'Icon' },
];

/**
 * @contract RunColumnMappingStepProps
 *
 * Purpose:
 * - Wire the Map Columns step to the run-flow state machine.
 *
 * Fields:
 * - flow: the `useImportRunFlow` result.
 */
export interface RunColumnMappingStepProps {
  flow: ImportRunFlow;
}

/**
 * @component RunColumnMappingStep
 *
 * Purpose:
 * - Render the run flow's Map Columns step.
 *
 * Props:
 * - RunColumnMappingStepProps.
 *
 * Behavior:
 * - Seeds lookup target fields once when entering lookup mode with no
 *   mappings; delegates all editing to RunWizardColumnMappingPanel.
 *
 * Data Source:
 * - Wizard state from the hook; suggestions via the services adapter.
 *
 * Constraints:
 * - No direct API calls; suggestion callbacks call `services.suggestMappings`
 *   / `services.interpretColumnGroups` on the host adapter.
 *
 * Performance:
 * - The seed effect is guarded on mapping count so it runs at most once.
 */
export default function RunColumnMappingStep({ flow }: RunColumnMappingStepProps) {
  const { state, patch, services } = flow;
  const columns = state.parseResult?.columns ?? [];
  const isLookup = state.dataMode === 'lookup';

  // Seed lookup target fields when entering lookup mode with no mappings.
  useEffect(() => {
    if (!isLookup || !state.parseResult || state.fieldMappings.length > 0) return;
    const suggested: FieldMapping[] = LOOKUP_TARGET_FIELDS.map(f => {
      const autoCol = columns.find(c => c.toLowerCase() === f.field.toLowerCase()) ?? '';
      return {
        targetField: f.field,
        sourceColumns: autoCol ? [autoCol] : [],
        isMatchKey: f.field === 'code' || f.field === 'lookupType',
      };
    });
    patch({ fieldMappings: suggested });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLookup, state.parseResult, state.fieldMappings.length]);

  if (!state.parseResult) {
    return (
      <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-4">
        Parse a file on the previous step before mapping columns.
      </div>
    );
  }

  const dataMode: ImportDataMode = state.dataMode ?? 'parcel';

  // Shell-based runs: the selected template is a shell (no parent, zero
  // mappings) and mappings beyond the pre-seeded match key are supplied at
  // import time. The banner stays visible while mapping so the user knows
  // their work applies to this run only unless they save a variant.
  const selectedShell =
    state.selectedTemplate &&
    !state.selectedTemplate.parentTemplateId &&
    (state.selectedTemplate.fieldMappingCount ?? 0) === 0;

  return (
    <div className="space-y-3">
      {selectedShell && (
        <div className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-md px-3 py-2">
          <strong>Shell template.</strong> Map this file's source columns to the target fields below.
          Mappings you set here apply to this import run only — at the end of the wizard you'll be able
          to save them back as a named variant of the shell if this file shape is something you expect to see again.
        </div>
      )}
      {isLookup ? (
        <p className="text-xs text-gray-500">Map source columns to lookup value fields. <strong>lookupType</strong>, <strong>code</strong>, and <strong>name</strong> are required.</p>
      ) : state.dataMode === 'parcelOwnerUpdate' ? (
        <p className="text-xs text-gray-500">
          Map source columns to <strong>ownership</strong> fields only — this import updates
          existing parcels and never creates them. Designate a <strong>match key</strong> (e.g.
          Parcel Number) and at least one ownership target. Optionally map
          <strong> Ownership Source Date</strong> to override the run&apos;s as-of date per row.
        </p>
      ) : (
        <p className="text-xs text-gray-500">
          Map each source column from your file to a target field. Mappings from the selected template are pre-filled — adjust or extend them as needed.
          Use <strong>Suggest Mappings</strong> for rule-based + AI-assisted auto-matching.
        </p>
      )}
      <RunWizardColumnMappingPanel
        fieldMappings={state.fieldMappings}
        onMappingsChange={(next) => patch({ fieldMappings: next })}
        dataMode={dataMode}
        availableColumns={columns}
        sampleRows={state.parseResult.sampleRows ?? []}
        suggestMappings={(params) => services.suggestMappings({
          dataMode: params.dataMode,
          columns: params.columns,
          sampleRows: params.sampleRows,
          tier: params.tier,
        })}
        interpretColumnGroups={services.interpretColumnGroups
          ? (params) => services.interpretColumnGroups!({
              dataMode: params.dataMode,
              columns: params.columns,
              sampleRows: params.sampleRows,
              groups: params.groups,
              autoDetect: params.autoDetect,
            })
          : undefined
        }
      />
    </div>
  );
}
